import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { TextField } from "./ui";
import { IconCheck, IconEye, IconEyeOff, IconWand } from "./icons";
import { useI18n, type TranslationKey } from "../i18n";
import { prefersReducedMotion } from "../motion";
import {
  PASSWORD_ALPHABET,
  PASSWORD_MIN,
  generatePassword,
  passwordScore,
} from "../password";
import "./PasswordFields.css";

const SCORE_LABEL: TranslationKey[] = [
  "auth.strengthTooShort",
  "auth.strengthWeak",
  "auth.strengthOkay",
  "auth.strengthGood",
  "auth.strengthStrong",
];
// Scramble: one character locks in every 38 ms (16 chars ≈ 0.6 s).
const SETTLE_MS_PER_CHAR = 38;
// How long the sheen that follows a generation stays on the fields.
const SHINE_MS = 900;

type GeneratedNote = "auth.passwordCopied" | "auth.passwordGenerated";

/** Folds a block in and out by height + blur, so the form grows and shrinks
 *  instead of jumping. Children stay mounted; while folded they are
 *  `visibility: hidden`, which also keeps them out of the tab order. */
function Reveal({ show, children }: { show: boolean; children: ReactNode }) {
  return (
    <div className={`pwf-reveal${show ? " pwf-reveal--open" : ""}`} aria-hidden={!show}>
      <div className="pwf-reveal__inner">{children}</div>
    </div>
  );
}

/** Password + repeat, with show/hide, a strength meter and one-click
 *  generation. Generating plays a short scramble — characters spin and lock
 *  in left to right, each flashing as it lands — then fills both fields,
 *  reveals them, runs a sheen across them and copies the password to the
 *  clipboard. The repeat field unfolds as soon as the first one is long
 *  enough. Used by sign-up and password reset. */
export function PasswordFields({
  password,
  confirm,
  onPasswordChange,
  onConfirmChange,
  placeholder,
  focused,
  autoFocus,
  disabled,
}: {
  password: string;
  confirm: string;
  onPasswordChange: (value: string) => void;
  onConfirmChange: (value: string) => void;
  placeholder: string;
  focused?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  // Mid-generation frame: `settled` characters are final, the rest still spin.
  const [scramble, setScramble] = useState<{ text: string; settled: number } | null>(null);
  const [shine, setShine] = useState(false);
  // The note keeps its text while it folds away, hence the separate flag.
  const [note, setNote] = useState<GeneratedNote>("auth.passwordCopied");
  const [noteShown, setNoteShown] = useState(false);
  const frame = useRef(0);
  const shineTimer = useRef(0);

  useEffect(
    () => () => {
      cancelAnimationFrame(frame.current);
      window.clearTimeout(shineTimer.current);
    },
    [],
  );

  const score = passwordScore(password);
  const scoreLabel = password ? t(SCORE_LABEL[score]) : "";
  const confirmVisible = password.length >= PASSWORD_MIN || confirm.length > 0;
  const matches = confirm.length > 0 && confirm === password;
  const mismatch = confirmTouched && confirm.length > 0 && confirm !== password;
  const generating = scramble !== null;

  const handleGenerate = useCallback(() => {
    cancelAnimationFrame(frame.current);
    window.clearTimeout(shineTimer.current);
    const pw = generatePassword();
    setVisible(true);
    setConfirmTouched(true);
    setNoteShown(false);
    setShine(false);

    const finish = async () => {
      setScramble(null);
      onPasswordChange(pw);
      onConfirmChange(pw);
      setShine(true);
      shineTimer.current = window.setTimeout(() => setShine(false), SHINE_MS);
      try {
        await navigator.clipboard.writeText(pw);
        setNote("auth.passwordCopied");
      } catch {
        setNote("auth.passwordGenerated");
      }
      setNoteShown(true);
    };

    if (prefersReducedMotion()) {
      void finish();
      return;
    }
    const start = performance.now();
    const tick = (now: number) => {
      const settled = Math.min(pw.length, Math.floor((now - start) / SETTLE_MS_PER_CHAR));
      let out = pw.slice(0, settled);
      for (let i = settled; i < pw.length; i++) {
        out += PASSWORD_ALPHABET[Math.floor(Math.random() * PASSWORD_ALPHABET.length)];
      }
      setScramble({ text: out, settled });
      if (settled < pw.length) frame.current = requestAnimationFrame(tick);
      else void finish();
    };
    frame.current = requestAnimationFrame(tick);
  }, [onPasswordChange, onConfirmChange]);

  const EyeIcon = visible ? IconEyeOff : IconEye;

  return (
    <div className={`pwf${visible ? " pwf--visible" : ""}`}>
      <button
        type="button"
        className={`pwf__generate${generating ? " pwf__generate--busy" : ""}`}
        onClick={handleGenerate}
        disabled={generating || disabled}
      >
        <IconWand width={14} height={14} strokeWidth={2} />
        {generating ? t("auth.generatingPassword") : t("auth.generatePassword")}
      </button>
      <TextField
        className={`${generating ? "pwf__main--generating" : ""}${shine ? " pwf__shine" : ""}`}
        placeholder={placeholder}
        focused={focused}
        type={visible ? "text" : "password"}
        value={scramble?.text ?? password}
        onChange={(e) => {
          setNoteShown(false);
          onPasswordChange(e.target.value);
        }}
        readOnly={generating}
        name="password"
        autoComplete="new-password"
        autoFocus={autoFocus}
        disabled={disabled}
        trailing={
          <>
            {scramble ? (
              // Painted over the (transparent) input text so each character
              // can carry its own colour.
              <span className="pwf__scramble" aria-hidden="true">
                {Array.from(scramble.text, (ch, i) => (
                  <span key={i} className={i < scramble.settled ? "pwf__ch pwf__ch--locked" : "pwf__ch"}>
                    {ch}
                  </span>
                ))}
              </span>
            ) : null}
            <button
              type="button"
              className="pwf__eye"
              onClick={() => setVisible((v) => !v)}
              aria-pressed={visible}
              aria-label={visible ? t("auth.hidePassword") : t("auth.showPassword")}
            >
              <EyeIcon width={18} height={18} />
            </button>
          </>
        }
      />
      <div className={`pwf__meter pwf__meter--s${score}`} aria-live="polite">
        <div className="pwf__bars">
          {[1, 2, 3, 4].map((i) => (
            <span key={i} className="pwf__bar">
              <span
                className={`pwf__fill${i <= score ? " pwf__fill--on" : ""}`}
                style={{ transitionDelay: i <= score ? `${(i - 1) * 60}ms` : "0ms" }}
              />
            </span>
          ))}
        </div>
        <span className="pwf__score">
          <span key={scoreLabel} className="pwf__score-text">
            {scoreLabel}
          </span>
        </span>
      </div>
      <Reveal show={noteShown}>
        <p className="pwf__note">
          <IconCheck key={String(noteShown)} width={12} height={12} strokeWidth={2.4} />
          {t(note)}
        </p>
      </Reveal>

      <Reveal show={confirmVisible}>
        <div className="pwf__confirm">
          <TextField
            className={`${matches ? "tf--ok" : ""}${shine ? " pwf__shine pwf__shine--late" : ""}`}
            placeholder={t("auth.repeatPassword")}
            type={visible ? "text" : "password"}
            value={confirm}
            onChange={(e) => {
              setConfirmTouched(true);
              onConfirmChange(e.target.value);
            }}
            name="password-confirm"
            autoComplete="new-password"
            disabled={disabled}
            trailing={
              matches ? (
                <span className="pwf__check" role="img" aria-label={t("auth.passwordsMatch")}>
                  <IconCheck width={18} height={18} strokeWidth={2} />
                </span>
              ) : null
            }
          />
          <Reveal show={mismatch}>
            <p className="pwf__mismatch">{t("auth.passwordsMismatch")}</p>
          </Reveal>
        </div>
      </Reveal>
    </div>
  );
}
